import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormsModule } from '@angular/forms';
import { NzFormDirective } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzModalComponent } from 'ng-zorro-antd/modal';
import { SEComponent, SEContainerComponent } from '@delon/abc/se';
import { ACLService } from '@delon/acl';
import { SharedModule } from '@shared';
import { QbotWidgetService } from '../../../../routes/ai/_providers/qbot-widget.service';

import { _HttpClient } from '@delon/theme';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MALAYSIA_COUNTRY_CODE,
  PHILIPPINES_COUNTRY_CODE,
  QUINNY_AI_AVATAR_URL
} from '@core';
import { debounceTime, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'app-q-bot-report-analyzer',
  standalone: true,
  imports: [
    FormsModule,
    NzFormDirective,
    NzInputDirective,
    NzModalComponent,
    SEComponent,
    SEContainerComponent,
    SharedModule,
    NzSpinModule,
    NzToolTipModule
  ],
  templateUrl: './q-bot-report-analyzer.component.html',
  styleUrl: `./q-bot-report-analyzer.component.less`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QBotReportAnalyzerComponent implements OnInit {
  readonly quinnyAiAvatarUrl = QUINNY_AI_AVATAR_URL;

  showBrowseReports = signal(false);

  qbotWidgetSrv = inject(QbotWidgetService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #acl = inject(ACLService);

  searchResults = signal<any[]>([]);
  showPremiumFeatureModal = signal(true);
  isSampleOrTrial = computed(() => this.qbotWidgetSrv.isSampleOrTrial());

  selectedTopPick = computed(() => this.qbotWidgetSrv.selectedTopPick());

  country = computed(() => this.qbotWidgetSrv.company()?.['country']);

  loading = signal(false);

  searchTermCtrl = new FormControl();
  http = inject(_HttpClient);

  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);

  onChangeSearchTerm() {
    this.searchTermCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.loading.set(true)),
        switchMap((searchTerm: string) => {
          const trimmedTerm = searchTerm?.toLowerCase()?.trim() || '';

          if (trimmedTerm === '' || trimmedTerm.length < 2) {
            this.loading.set(false);
            return of([]);
          }

          this.loading.set(true);

          return this.http.get(`api/ReportCenter/Menu?name=${trimmedTerm}`);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: value => {
          this.loading.set(false);
          if (!value) {
            this.searchResults.set([]);
            return;
          }
          this.searchResults.set([...this.mapToFlatReports(value)]);
        },
        error: () => {
          this.loading.set(false);
        }
      });
  }

  canView(acl: string): boolean {
    return this.#acl.canAbility(acl);
  }

  onReportClicked(path: any) {
    this.qbotWidgetSrv.selectedTopPick.set(path);
    this.#cdr.detectChanges();
    this.showBrowseReports.set(false);
    this.searchTermCtrl.setValue(null);
    this.#router.navigate([path]);
  }

  showBrowseModal() {
    this.showBrowseReports.set(true);
    this.searchTermCtrl.setValue(null);
  }

  smallFont() {
    return this.country() === PHILIPPINES_COUNTRY_CODE;
  }

  onClose() {
    this.qbotWidgetSrv.selectedTopPick.set(null);
    this.showBrowseReports.set(false);
    this.searchTermCtrl.setValue(null);
    this.qbotWidgetSrv.showReportAnalyzerWidget.set(false);
  }

  onUpgrade() {
    this.qbotWidgetSrv.upgradeFromAiLayouts();
  }

  mappedTopPicks = computed(() => {
    return this.topPicks().filter(tp => tp.country === this.country());
  });

  topPicks = signal([
    {
      name: 'Profit and Loss',
      path: '/reporting/report/gl/pnl-details',
      acl: 'pnl-details.view',
      country: MALAYSIA_COUNTRY_CODE
    },
    {
      name: '12 Months Profit and Loss',
      path: '/reporting/report/gl/pnl-12months',
      acl: 'pnl-12months.view',
      country: MALAYSIA_COUNTRY_CODE
    },
    {
      name: ' Profit and Loss (Budget vs Actual)',
      path: '/reporting/report/gl/pnl-budget',
      acl: 'pnl-budget.view',
      country: MALAYSIA_COUNTRY_CODE
    },
    {
      name: 'Balance Sheet',
      path: '/reporting/report/gl/balance-sheet-details',
      acl: 'balance-sheet-details.view',
      country: MALAYSIA_COUNTRY_CODE
    },
    {
      name: 'Customer Aging',
      path: '/reporting/report/ar/ar-aging-12',
      acl: 'ar-aging-12.view',
      country: MALAYSIA_COUNTRY_CODE
    },
    {
      name: 'Supplier Aging',
      path: '/reporting/report/ap/ap-aging-12',
      acl: 'ap-aging-12.view',
      country: MALAYSIA_COUNTRY_CODE
    },

    {
      name: 'Statement of Comprehensive Income',
      path: '/reporting/report/gl/pnl-details',
      acl: 'pnl-details.view',
      country: PHILIPPINES_COUNTRY_CODE
    },
    {
      name: 'Statement of Comprehensive Income With Budget',
      path: '/reporting/report/gl/pnl-budget',
      acl: 'pnl-budget.view',
      country: PHILIPPINES_COUNTRY_CODE
    },
    {
      name: 'Statement of Comprehensive Income Comparison',
      path: '/reporting/report/gl/pnl-comparison',
      acl: 'pnl-comparison.view',
      country: PHILIPPINES_COUNTRY_CODE
    },
    {
      name: 'Statement of Comprehensive Income Comparison 5 Periods',
      path: '/reporting/report/gl/pnl-comparison-5-periods',
      acl: 'pnl-comparison-5-periods.view',
      country: PHILIPPINES_COUNTRY_CODE
    },
    {
      name: '12 Months Statement of Comprehensive Income',
      path: '/reporting/report/gl/pnl-12months',
      acl: 'pnl-12months.view',
      country: PHILIPPINES_COUNTRY_CODE
    },
    {
      name: 'Statement of Financial Position',
      path: '/reporting/report/gl/balance-sheet-details',
      acl: 'balance-sheet-details.view',
      country: PHILIPPINES_COUNTRY_CODE
    }
  ]);

  ngOnInit(): void {
    this.showPremiumFeatureModal.set(true);
    this.onChangeSearchTerm();
  }

  /**
   * Maps a nested report structure to a flat array of objects with name and path.
   * @param reports The input array of nested report objects.
   * @returns An array of flat report objects with name and path.
   */
  mapToFlatReports(reports: any[]): any[] {
    const flatReports: any[] = [];

    const flatten = (items: any[] | null) => {
      if (!items) return;
      for (const item of items) {
        // Include only items with a non-empty path
        if (item.path && item.path !== '') {
          flatReports.push({
            name: item.name,
            path: item.path
          });
        }
        // Recursively process children
        if (item.children) {
          flatten(item.children);
        }
      }
    };

    flatten(reports);
    return flatReports;
  }
}
